import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { hasTaskTag, lookFamily, type SettingLookCode } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import type { AreaRow } from '../../data/geo.ts'
import type { GeoJsonGeometry } from '../../lib/geo.ts'
import { frameFor, type LookFrame } from '../../data/lookGeometry.ts'
import { settingLines, type PlanJob, type SettingLine, type TaskTypeRow } from '../../data/plan.ts'
import { areaShort, sortedAreaIds } from '../../data/planUi.ts'
import { PLAY_LINE_TYPES, usePlayLines } from '../../data/playLine.ts'
import { confirmationsOfJob, doneLines, type DoneLine } from '../../data/settingsCompare.ts'
import { useSettingsConfirmations } from '../../data/settingsDone.ts'
import { buildHistory } from '../../data/settingsHistory.ts'
import { LookPicture } from '../../ui/looks/LookPicture.tsx'
import { OutlineSchema } from '../../ui/looks/OutlineSchema.tsx'
import { useReadsWork, useSettingsData, useToday } from './useSettingsData.ts'

/** M25-D (docs/M25.md E, rules 5 and 9): THE SETTINGS OF A JOB AS EVERY SURFACE OF THE PLAN SHOWS THEM — the lines in the order the type declares them
 *  (`settingLines`), each with the picture the club chose for its word beside the label and the word (the words carry the meaning; the picture is aria-hidden).
 *  The generic shape a picture is drawn on is chosen from the job's area types (`frameOfAreas`): a picture only, nothing decides on it. */

/** The generic frame of a job: from the types of its areas (`frameFor`; round when the device does not know them). */
export const frameOfAreas = (areaIds: readonly string[], areaById: ReadonlyMap<string, Pick<AreaRow, 'area_type_code'>>): LookFrame =>
  frameFor(areaIds.map((id) => areaById.get(id)?.area_type_code ?? ''))

const valueText = (l: { value: string | number; unit: string | null }) => `${l.value}${l.unit ? ' ' + l.unit : ''}`

/** The settings of a job, one line each: « [picture] Sens de tonte Diagonale droite ». `size`: the card's 28-px pictograms, or the shed screen's 48 px. */
export function SettingLinesView({ lines, frame = 'round', size = 'card', testId, className = '', pictureClass = 'text-brand dark:text-fairway' }: {
  lines: readonly SettingLine[]; frame?: LookFrame; size?: 'card' | 'tv'; testId?: string; className?: string; pictureClass?: string
}) {
  if (lines.length === 0) return null
  const pic = `${size === 'tv' ? 'size-[48px]' : 'size-7'} shrink-0 ${pictureClass}`
  return (
    <ul className={`flex flex-col ${size === 'tv' ? 'gap-2' : 'gap-1'} ${className}`} data-testid={testId}>
      {lines.map((l) => (
        <li key={l.key} className="flex items-center gap-2" data-setting={l.key}>
          {l.look && <LookPicture code={l.look} frame={frame} className={pic} />}
          <span><span>{l.label}</span>{' '}<span className="font-semibold">{valueText(l)}</span></span>
        </li>
      ))}
    </ul>
  )
}

/** What was confirmed at the finish, in the same shape: the picture, the label, the club's word as it was tapped (« (n'est plus un choix) » when it no longer is),
 *  and a sand dot with its words where it differs from the plan. */
export function DoneLinesView({ lines, frame = 'round', testId }: { lines: readonly DoneLine[]; frame?: LookFrame; testId?: string }) {
  const { t } = useTranslation()
  if (lines.length === 0) return null
  return (
    <ul className="flex flex-col gap-1" data-testid={testId}>
      {lines.map((l) => (
        <li key={l.key} className="flex items-center gap-2" data-setting={l.key} data-differs={l.differs || undefined}>
          {l.look && <LookPicture code={l.look} frame={frame} className="size-7 shrink-0 text-brand dark:text-fairway" />}
          <span>
            <span>{l.label}</span>{' '}<span className="font-semibold">{valueText(l)}</span>
            {l.stale && <span className="meta"> {t('settingsDone.notAChoice')}</span>}
          </span>
          {l.differs && (
            <>
              <span aria-hidden="true" data-mark-dot="filled" className="size-3 shrink-0 rounded-full bg-sand-ink dark:bg-sand" />
              <span className="sr-only">{t('plan.settingsView.differs')}</span>
            </>
          )}
        </li>
      ))}
    </ul>
  )
}

/** The « Réglages » section of a job's page: the settings with their pictures; for a FINISHED job whose finish was confirmed, « Prévu » beside « Fait » with the
 *  dot (rule 3: what was really done is a fact beside the plan, never written over it). The viewer's own confirmations always show; those of other people only on
 *  a device that holds them (`worklog.read`). With none read, the plan alone is shown, and « prévu, non confirmé » is said only where the device holds every
 *  finish and the type asks for the confirmation (never for work this device cannot see). Names nobody: a crew of two who say the same thing is one line. */
export function JobSettingsSection({ job, tt, lang, frame }: { job: PlanJob; tt: TaskTypeRow | undefined; lang: string; frame: LookFrame }) {
  const { t } = useTranslation()
  const { can } = useClub()
  const rows = useSettingsConfirmations()
  const finished = job.state === 'done' || job.state === 'partially_done'
  const mine = useMemo(() => (finished && rows ? confirmationsOfJob(rows, job.id) : []), [finished, rows, job.id])
  const planned = useMemo(() => settingLines(job.params, tt, lang), [job.params, tt, lang])
  if (planned.length === 0 && mine.length === 0) return null
  const reads = can('worklog.read')
  return (
    <div className="flex flex-col gap-2" data-testid="job-settings">
      <h2 className="text-lg font-bold">{t('plan.settings')}</h2>
      {mine.length === 0 ? (
        <>
          <SettingLinesView lines={planned} frame={frame} testId="job-params" />
          {finished && reads && planned.length > 0 && hasTaskTag(tt, 'confirm_settings') && (
            <p className="meta" data-testid="job-unconfirmed">{t('plan.settingsView.unconfirmed')}. {t('plan.settingsView.unconfirmedHelp')}</p>
          )}
        </>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="job-done-settings">
          <div className="flex flex-col gap-1" data-testid="job-planned-lines">
            <h3 className="font-bold">{t('plan.settingsView.planned')}</h3>
            {planned.length > 0 ? <SettingLinesView lines={planned} frame={frame} testId="job-params" /> : <p className="meta">—</p>}
          </div>
          <div className="flex flex-col gap-2" data-testid="job-done-lines">
            <h3 className="font-bold">{t('plan.settingsView.done')}</h3>
            {mine.map((c) => <DoneLinesView key={c.id} lines={doneLines(job.params, tt, c, lang)} frame={frame} />)}
          </div>
        </div>
      )}
    </div>
  )
}

/** The picture of the previous cut, per choice setting, for the form's dial (dashed): the most recent history line on an area of the job before its day. Only where
 *  the viewer holds `job.plan` and `worklog.read` (rule 2: nowhere else is the history on the device); empty otherwise. */
export function usePreviousLooks(tt: TaskTypeRow | undefined, areaIds: readonly string[], until: string): Readonly<Record<string, SettingLookCode>> {
  const { site } = useClub()
  const reads = useReadsWork()
  const data = useSettingsData()
  const today = useToday()
  return useMemo(() => {
    if (!reads || !data || !tt || areaIds.length === 0 || !until) return {}
    const history = buildHistory({
      tt, jobs: data.jobs, jobAreas: data.jobAreas, completions: data.completions, coverage: data.coverage, confirmations: data.confirmations,
      areaIds, today, until, tz: site.timezone, readsWork: true,
    })
    const out: Record<string, SettingLookCode> = {}
    const best = new Map<string, string>()
    for (const l of history.lines) {
      const stamp = `${l.day}|${l.at}`
      for (const [key, code] of Object.entries(l.codes)) if (stamp >= (best.get(key) ?? '')) { best.set(key, stamp); out[key] = code }
    }
    return out
  }, [reads, data, tt, areaIds, until, today, site.timezone])
}

const MAX_TILES = 24
/** « Sur chaque green » (the job page and the timer): each area of the job drawn with its REAL outline, turned so that its line of play points up, the stripes
 *  laid across it at the job's direction (rules 5 and 6). An area whose line of play the map cannot give is not drawn turned: it is named once, said unknown,
 *  with the generic drawing — never guessed. Nothing when the job has no direction, pattern or lap picture. A fold: closed until asked. */
export function EachGreen({ areaIds, lines, areaById, lang, testId = 'each-green' }: {
  areaIds: readonly string[]; lines: readonly SettingLine[]; areaById: Map<string, AreaRow>; lang: string; testId?: string
}) {
  const { t } = useTranslation()
  const data = usePlayLines()
  const look = lines.find((l) => l.look && (lookFamily(l.look) === 'dir' || lookFamily(l.look) === 'pat'))?.look ?? lines.find((l) => l.look && lookFamily(l.look) === 'lap')?.look ?? null
  const { known, unknown } = useMemo(() => {
    const known: string[] = [], unknown: string[] = []
    if (!data) return { known, unknown }
    for (const id of sortedAreaIds([...areaIds], areaById, lang)) {
      const type = areaById.get(id)?.area_type_code
      if (!type || !(PLAY_LINE_TYPES as readonly string[]).includes(type)) continue
      const l = data.lines.get(id)
      if (l && l.bearing !== null && data.shapes.has(id)) known.push(id)
      else unknown.push(id)
    }
    return { known, unknown }
  }, [data, areaIds, areaById, lang])
  if (!look || !data || known.length + unknown.length === 0) return null
  const name = (id: string) => areaShort(areaById.get(id), areaById, lang, t)
  const shown = known.slice(0, MAX_TILES)
  const generic = unknown.slice(0, Math.max(0, MAX_TILES - shown.length))
  const hidden = known.length + unknown.length - shown.length - generic.length
  const names = unknown.slice(0, 6).map(name).join(', ')
  const tile = (id: string, bearing: number | null, geom: GeoJsonGeometry | undefined) => (
    <li key={id} className="flex w-24 flex-col items-center gap-1 text-center" data-area={id} data-outline-of={bearing === null ? 'generic' : 'real'}>
      <OutlineSchema geom={geom} bearing={bearing} code={look} frame={frameFor([areaById.get(id)?.area_type_code ?? ''])} playArrow={bearing !== null} showUnknown={false} className="size-20 shrink-0 text-brand dark:text-fairway" />
      <span className="meta">{name(id)}</span>
    </li>
  )
  return (
    <details className="card" data-testid={testId} data-known={known.length} data-unknown={unknown.length}>
      <summary className="flex min-h-14 cursor-pointer items-center text-lg font-bold" data-testid={`${testId}-toggle`}>{t('plan.eachGreen.title')}</summary>
      <div className="flex flex-col gap-3 pt-1">
        <p className="meta">{t('plan.eachGreen.help')}</p>
        <ul className="flex flex-wrap gap-3" data-testid={`${testId}-tiles`}>
          {shown.map((id) => tile(id, data.lines.get(id)!.bearing, data.shapes.get(id)))}
          {generic.map((id) => tile(id, null, undefined))}
        </ul>
        {hidden > 0 && <p className="meta">{t('plan.eachGreen.more', { count: hidden })}</p>}
        {unknown.length > 0 && <p className="meta" data-testid={`${testId}-unknown`}>{t('plan.eachGreen.unknown', { count: unknown.length, names: unknown.length > 6 ? `${names}, ${t('plan.eachGreen.more', { count: unknown.length - 6 })}` : names })}</p>}
      </div>
    </details>
  )
}
